import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
const AUTH_API_URL = `${SERVER_DOMAIN}/api/auth`;

// Toggle 2FA
export const toggle2FA = createAsyncThunk(
  "twoFactor/toggle2FA",
  async (enabled, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue({ message: "No token found for authentication" });
      }

      const response = await axios.post(
        `${AUTH_API_URL}/toggle-2fa`,
        { enabled },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to toggle 2FA",
      });
    }
  }
);

// Verify OTP during login
export const verifyOTP = createAsyncThunk(
  "twoFactor/verifyOTP",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${AUTH_API_URL}/verify-otp`,
        { email, otp }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Invalid OTP",
      });
    }
  }
);

// Resend OTP
export const resendOTP = createAsyncThunk(
  "twoFactor/resendOTP",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${AUTH_API_URL}/resend-otp`,
        { email }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to resend OTP",
      });
    }
  }
);

const twoFactorSlice = createSlice({
  name: "twoFactor",
  initialState: {
    // null = "not toggled this session yet" - components fall back to
    // userDetails.twoFactorEnabled (from GET /api/auth/user) until this is set.
    twoFactorEnabled: null,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Toggle 2FA
      .addCase(toggle2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(toggle2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.twoFactorEnabled = action.payload.twoFactorEnabled;
        state.successMessage = action.payload.message;
        state.error = null;
      })
      .addCase(toggle2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to toggle 2FA";
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Invalid OTP";
      })
      // Resend OTP
      .addCase(resendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to resend OTP";
      });
  },
});

export const { clearMessages } = twoFactorSlice.actions;
export default twoFactorSlice.reducer;

